import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useLandingTranslation } from "@tablemint/i18n";
import { Button } from "@tablemint/ui/src/components/Button";
import AnimatedSection from "../components/animations/AnimatedSection";
import { staggerContainer, fadeInUp } from "../utils/animation";
import { Copy, Check } from "lucide-react";

// Define business types
const BUSINESS_TYPES = [
  { value: "restaurant", label: "Restaurant" },
  { value: "shishaBar", label: "Shisha Bar" },
  { value: "cafe", label: "Cafe" },
  { value: "barber", label: "Barber Shop" },
  { value: "salon", label: "Salon" },
  { value: "spa", label: "Spa" },
];

// Define color presets
const COLOR_PRESETS = [
  { name: "primary", value: "#3EDBB7", contrastText: "#ffffff" },
  { name: "blue", value: "#3b82f6", contrastText: "#ffffff" },
  { name: "purple", value: "#8b5cf6", contrastText: "#ffffff" },
  { name: "red", value: "#ef4444", contrastText: "#ffffff" },
  { name: "amber", value: "#f59e0b", contrastText: "#ffffff" },
  { name: "teal", value: "#14b8a6", contrastText: "#ffffff" },
  { name: "pink", value: "#ec4899", contrastText: "#ffffff" },
  { name: "indigo", value: "#6366f1", contrastText: "#ffffff" },
  { name: "black", value: "#1f2937", contrastText: "#ffffff" },
];

const FeaturesPage = () => {
  const { t, i18n, ready } = useLandingTranslation();

  // State for the widget editor
  const [businessType, setBusinessType] = useState("restaurant");
  const [colorScheme, setColorScheme] = useState(COLOR_PRESETS[0]); // Default to primary (mint green)
  const [widgetTitle, setWidgetTitle] = useState("Book a Table");
  const [showPartySize, setShowPartySize] = useState(true);
  const [enableDeposit, setEnableDeposit] = useState(false);
  const [showTimeSlots, setShowTimeSlots] = useState(true);
  const [viewMode, setViewMode] = useState("desktop"); // "desktop" or "mobile"
  const [embedOption, setEmbedOption] = useState("code"); // "code" or "link"
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Debug localization
  useEffect(() => {
    console.log("i18n info:", {
      currentLanguage: i18n.language,
      isInitialized: i18n.isInitialized,
      isLoaded: ready,
      availableLanguages: i18n.languages,
    });

    // Check specific widget translations
    console.log("widget translations available:", {
      "features.widget.title": t("features.widget.title"),
      "features.widget.subtitle": t("features.widget.subtitle"),
      "features.widget.customizeTitle": t("features.widget.customizeTitle"),
      "features.widget.businessType": t("features.widget.businessType"),
      // Check actual translation object
      raw: i18n.getResourceBundle(i18n.language, "landing"),
    });
  }, [i18n, ready, t]);

  // Function to generate embed code
  const generateEmbedCode = () => {
    return `<script>
  (function(d, s, id) {
    var js, tjs = d.getElementsByTagName(s)[0];
    if (d.getElementById(id)) return;
    js = d.createElement(s); js.id = id;
    js.src = "https://tablemint.app/widget.js";
    js.setAttribute("data-business-type", "${businessType}");
    js.setAttribute("data-color", "${colorScheme.value}");
    js.setAttribute("data-title", "${widgetTitle}");
    js.setAttribute("data-show-party-size", "${showPartySize}");
    js.setAttribute("data-enable-deposit", "${enableDeposit}");
    js.setAttribute("data-show-time-slots", "${showTimeSlots}");
    tjs.parentNode.insertBefore(js, tjs);
  }(document, "script", "tablemint-widget"));
</script>`;
  };

  // Function to generate direct link URL
  const generateDirectLink = () => {
    const baseUrl = "https://tablemint.app/book";
    const params = new URLSearchParams({
      business: businessType,
      color: colorScheme.value,
      title: encodeURIComponent(widgetTitle),
      partySize: showPartySize.toString(),
      deposit: enableDeposit.toString(),
      timeSlots: showTimeSlots.toString(),
    });
    return `${baseUrl}?${params.toString()}`;
  };

  // Function to handle copy button click
  const handleCopyCode = () => {
    const textToCopy =
      embedOption === "code" ? generateEmbedCode() : generateDirectLink();
    navigator.clipboard.writeText(textToCopy);

    // Visual feedback for copy action
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  // Format date for display
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat(i18n.language || "en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    }).format(date);
  };

  // Generate array of next 7 days for date selection
  const getNextDays = () => {
    const days = [];
    const today = new Date();

    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(today.getDate() + i);
      days.push(date);
    }

    return days;
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
      {/* Main Feature */}
      <AnimatedSection>
        <div className="relative isolate overflow-hidden bg-white dark:bg-gray-800 shadow-sm mt-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-32">
            <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2">
              <div className="lg:pr-8 lg:pt-4">
                <div className="lg:max-w-lg">
                  <h2 className="text-base font-semibold leading-7 text-primary">
                    {t("features.mainFeature.title")}
                  </h2>
                  <p className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
                    {t("features.mainFeature.title")}
                  </p>
                  <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300">
                    {t("features.mainFeature.description")}
                  </p>
                  <dl className="mt-10 max-w-xl space-y-8 text-base leading-7 text-gray-600 dark:text-gray-300 lg:max-w-none">
                    <div className="relative pl-9">
                      <dt className="inline font-semibold text-gray-900 dark:text-white">
                        <span className="absolute left-1 top-1 h-5 w-5 text-primary">
                          ✓
                        </span>
                      </dt>
                      <dd className="inline">
                        {t("features.mainFeature.customizable")}
                      </dd>
                    </div>
                    <div className="relative pl-9">
                      <dt className="inline font-semibold text-gray-900 dark:text-white">
                        <span className="absolute left-1 top-1 h-5 w-5 text-primary">
                          ✓
                        </span>
                      </dt>
                      <dd className="inline">
                        {t("features.mainFeature.integration")}
                      </dd>
                    </div>
                    <div className="relative pl-9">
                      <dt className="inline font-semibold text-gray-900 dark:text-white">
                        <span className="absolute left-1 top-1 h-5 w-5 text-primary">
                          ✓
                        </span>
                      </dt>
                      <dd className="inline">
                        {t("features.mainFeature.responsive")}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
              <div>
                <img
                  src="/images/dashboard-screenshot.png"
                  alt={t("features.mainFeature.imageAlt", "Product screenshot")}
                  className="w-full max-w-[48rem] rounded-xl shadow-xl ring-1 ring-gray-200 dark:ring-white/10 object-cover mx-auto"
                  width={2432}
                  height={1442}
                />
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Widget Customization */}
      <AnimatedSection>
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-32">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              {t("features.widget.title")}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              {t("features.widget.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Customization Options - Left Panel */}
            <div className="lg:col-span-5 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-6">
                {t("features.widget.customizeTitle")}
              </h3>

              {/* Business Type */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  {t("features.widget.businessType")}
                </label>
                <select
                  className="w-full bg-white dark:bg-gray-700 rounded-md border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white py-2 px-3"
                  value={businessType}
                  onChange={(e) => setBusinessType(e.target.value)}
                >
                  {BUSINESS_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {t(
                        `features.widget.businessTypes.${type.value}`,
                        type.label
                      )}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t("features.widget.businessTypeHelp")}
                </p>
              </div>

              {/* Color Scheme */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  {t("features.widget.colorScheme")}
                </label>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {COLOR_PRESETS.map((color) => (
                    <div
                      key={color.name}
                      className={`h-8 rounded-md cursor-pointer border-2 ${
                        colorScheme.name === color.name
                          ? "border-primary ring-2 ring-primary/30"
                          : "border-transparent"
                      }`}
                      style={{ backgroundColor: color.value }}
                      onClick={() => setColorScheme(color)}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t("features.widget.colorSchemeHelp")}
                </p>
              </div>

              {/* Widget Title */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  {t("features.widget.widgetTitle")}
                </label>
                <input
                  type="text"
                  className="w-full bg-white dark:bg-gray-700 rounded-md border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white py-2 px-3"
                  value={widgetTitle}
                  onChange={(e) => setWidgetTitle(e.target.value)}
                  placeholder={t("features.widget.widgetTitlePlaceholder")}
                />
              </div>

              {/* Booking Options */}
              <div className="mb-8">
                <label className="block text-sm font-medium mb-3">
                  {t("features.widget.bookingOptions")}
                </label>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="showPartySize"
                      checked={showPartySize}
                      onChange={() => setShowPartySize(!showPartySize)}
                      className="h-4 w-4 text-primary border-gray-300 rounded mr-2"
                    />
                    <label htmlFor="showPartySize" className="text-sm">
                      {t("features.widget.showPartySize")}
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enableDeposit"
                      checked={enableDeposit}
                      onChange={() => setEnableDeposit(!enableDeposit)}
                      className="h-4 w-4 text-primary border-gray-300 rounded mr-2"
                    />
                    <label htmlFor="enableDeposit" className="text-sm">
                      {t("features.widget.enableDeposit")}
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="showTimeSlots"
                      checked={showTimeSlots}
                      onChange={() => setShowTimeSlots(!showTimeSlots)}
                      className="h-4 w-4 text-primary border-gray-300 rounded mr-2"
                    />
                    <label htmlFor="showTimeSlots" className="text-sm">
                      {t("features.widget.showTimeSlots")}
                    </label>
                  </div>
                </div>
              </div>

              {/* Embed Options */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  {t("features.widget.embedOption", "Embed Option")}
                </label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setEmbedOption("code")}
                    className={`flex-1 py-2 px-3 text-sm font-medium rounded-md ${
                      embedOption === "code"
                        ? "bg-primary text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {t("features.widget.embedCode", "Code Snippet")}
                  </button>
                  <button
                    onClick={() => setEmbedOption("link")}
                    className={`flex-1 py-2 px-3 text-sm font-medium rounded-md ${
                      embedOption === "link"
                        ? "bg-primary text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {t("features.widget.directLink", "Direct Link")}
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {embedOption === "code"
                    ? t(
                        "features.widget.embedCodeHelp",
                        "Add this code to your website to embed the widget directly."
                      )
                    : t(
                        "features.widget.directLinkHelp",
                        "Share this link with your customers to book on our hosted page."
                      )}
                </p>
              </div>

              {/* Embed Code or Direct Link */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  {embedOption === "code"
                    ? t("features.widget.embedCode", "Embed Code")
                    : t("features.widget.directLink", "Direct Link")}
                </label>
                <div className="relative">
                  <pre className="bg-gray-50 dark:bg-gray-700 rounded-md p-3 overflow-x-auto text-xs shadow-inner">
                    <code className="text-gray-800 dark:text-gray-200">
                      {embedOption === "code"
                        ? generateEmbedCode()
                        : generateDirectLink()}
                    </code>
                  </pre>
                  <button
                    onClick={handleCopyCode}
                    className="absolute top-2 right-2 bg-primary hover:bg-primary/90 text-white text-xs py-1 px-2 rounded flex items-center gap-1 transition-all duration-200"
                  >
                    {isCopied ? (
                      <>
                        <Check className="h-3 w-3" />
                        {t("features.widget.copied", "Copied!")}
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" />
                        {t("features.widget.copyCode", "Copy")}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Preview - Right Panel */}
            <div className="lg:col-span-7">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 h-full">
                {/* Preview Controls */}
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold">
                    {t("features.widget.preview", "Preview")}
                  </h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setViewMode("desktop")}
                      className={`px-3 py-1 rounded-md text-sm font-medium ${
                        viewMode === "desktop"
                          ? "bg-primary text-white"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {t("features.widget.desktop")}
                    </button>
                    <button
                      onClick={() => setViewMode("mobile")}
                      className={`px-3 py-1 rounded-md text-sm font-medium ${
                        viewMode === "mobile"
                          ? "bg-primary text-white"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {t("features.widget.mobile")}
                    </button>
                  </div>
                </div>

                {/* Preview Container */}
                <div
                  className={`
                  bg-white rounded-lg p-4 mx-auto border shadow-sm
                  ${viewMode === "mobile" ? "max-w-[360px]" : "max-w-[720px]"}
                `}
                >
                  {/* Widget Header */}
                  <div
                    className="rounded-t-md p-4 text-center"
                    style={{
                      backgroundColor: colorScheme.value,
                      color: colorScheme.contrastText,
                    }}
                  >
                    <h4 className="text-lg font-bold">{widgetTitle}</h4>
                  </div>

                  {/* Widget Body */}
                  <div className="p-4 text-gray-800">
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1 text-gray-700">
                        {t("features.widget.date", "Date")}
                      </label>

                      {/* Calendar Selection */}
                      <div className="relative">
                        <button
                          onClick={() => setShowCalendar(!showCalendar)}
                          className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm bg-white hover:bg-gray-50 text-left flex justify-between items-center"
                        >
                          <span>{formatDate(selectedDate)}</span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>

                        {showCalendar && (
                          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200">
                            <div className="p-2">
                              <div className="grid grid-cols-1 gap-1">
                                {getNextDays().map((date, index) => (
                                  <button
                                    key={index}
                                    onClick={() => {
                                      setSelectedDate(date);
                                      setShowCalendar(false);
                                    }}
                                    className={`text-left py-2 px-3 rounded-md text-sm hover:bg-gray-100 ${
                                      date.toDateString() ===
                                      selectedDate.toDateString()
                                        ? "bg-primary/10 font-medium"
                                        : ""
                                    }`}
                                  >
                                    {index === 0
                                      ? t("features.widget.today", "Today")
                                      : index === 1
                                        ? t(
                                            "features.widget.tomorrow",
                                            "Tomorrow"
                                          )
                                        : formatDate(date)}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1 text-gray-700">
                        {t("features.widget.time", "Time")}
                      </label>
                      {showTimeSlots && (
                        <div className="grid grid-cols-3 gap-2">
                          <button className="border border-gray-300 rounded-md py-2 px-3 text-sm bg-white hover:bg-gray-50">
                            18:00
                          </button>
                          <button className="border border-gray-300 rounded-md py-2 px-3 text-sm bg-white hover:bg-gray-50">
                            19:00
                          </button>
                          <button className="border border-gray-300 rounded-md py-2 px-3 text-sm bg-white hover:bg-gray-50">
                            20:00
                          </button>
                        </div>
                      )}
                    </div>

                    {showPartySize && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-1 text-gray-700">
                          {t("features.widget.partySize", "Party Size")}
                        </label>
                        <select className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm">
                          <option>
                            1 {t("features.widget.person", "person")}
                          </option>
                          <option>
                            2 {t("features.widget.people", "people")}
                          </option>
                          <option>
                            3 {t("features.widget.people", "people")}
                          </option>
                          <option>
                            4 {t("features.widget.people", "people")}
                          </option>
                          <option>
                            5+ {t("features.widget.people", "people")}
                          </option>
                        </select>
                      </div>
                    )}

                    {enableDeposit && (
                      <div className="mb-4 bg-gray-50 p-3 rounded-md text-sm text-gray-600">
                        {t(
                          "features.widget.depositNote",
                          "A small deposit is required to secure your booking. This will be deducted from your final bill."
                        )}
                      </div>
                    )}

                    <button
                      className="w-full rounded-md py-2 px-4 text-sm font-medium mt-2"
                      style={{
                        backgroundColor: colorScheme.value,
                        color: colorScheme.contrastText,
                      }}
                    >
                      {t("features.widget.bookButton", "Find Available Times")}
                    </button>

                    <div className="text-center text-xs text-gray-500 mt-3">
                      {t("features.widget.poweredBy", "Powered by")} Tablemint
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-20 text-center">
            <h3 className="text-2xl font-bold mb-4">
              {t(
                "features.widget.ctaTitle",
                "Ready to Transform Your Booking Experience?"
              )}
            </h3>
            <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300 mb-8">
              {t(
                "features.widget.ctaDescription",
                "Join thousands of businesses already using Tablemint to streamline their bookings and delight their customers."
              )}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white font-medium px-8 py-3 text-lg rounded-lg shadow-md hover:shadow-lg transition-all"
                onClick={() => (window.location.href = "/signup")}
              >
                {t("features.widget.trialButton", "Start Free Trial")}
              </Button>
              <Button
                size="lg"
                className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium px-8 py-3 text-lg rounded-lg shadow-sm hover:shadow-md transition-all"
                onClick={() => (window.location.href = "/pricing")}
              >
                {t("features.widget.viewPricing", "View Pricing Plans")}
              </Button>
            </div>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default FeaturesPage;
